import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Accordion } from '@domgen/base-design-system-2.0/atoms/Accordion';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { MouseEventHandler, ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { Lock } from '../../../assets/icons/Lock';
import { AppRoute } from '../../../enums/appRoutes';
import { useConfirmation } from '../../../store/selectors/useConfirmation';
import { CHECKOUT_FAQ_ITEMS } from '../constants/checkoutFaqItems';
import { useBillingAddressPlacement } from '../hooks/useBillingAddressPlacement';
import { useCheckoutTracking } from '../hooks/useCheckoutTracking';
import { useConsentBoxes } from '../hooks/useConsentBoxes';
import { useFieldValidationState } from '../hooks/useFieldValidationState';
import { useMobileSummarySheet } from '../hooks/useMobileSummarySheet';
import { usePaymentMethodSelection } from '../hooks/usePaymentMethodSelection';
import { useSdkCopy } from '../hooks/useSdkCopy';
import { useSdkSlots } from '../hooks/useSdkSlots';
import { useSelectedMethodScroll } from '../hooks/useSelectedMethodScroll';
import { useSummaryTotalCopy } from '../hooks/useSummaryTotalCopy';
import { getAchPromotionText } from '../utils/getAchPromotionText';
import { CheckoutBillingHint } from './CheckoutBillingHint';
import { CheckoutConsent } from './CheckoutConsent';
import './CheckoutVariantLayout.css';

const FAQ_SECTION_ID = 'checkout-faq';
const FAQ_HEADING_ID = 'checkout-faq-heading';
const ITEM_BENEFITS_SLOT = {
  selector: '#gc-dg-appliances .gc-dg-inner-card-container',
  className: 'checkout-v2__item-benefits',
};
const ITEM_BENEFITS = ['A new replacement if we can’t fix it', 'Unlimited repairs, $0 deductible'];
const BILLING_HINT_SLOT = {
  selector: '#gc-dg-billing_address',
  className: 'checkout-v2__billing-hint',
};

const styles = {
  page: 'checkout-v2 [--checkout-main-column:40.5rem] bg-bg-main tablet:py-space-600 tablet:px-space-400 desktop:py-space-800',
  card: 'max-w-300 mx-auto bg-bg-main-container p-space-500 tablet:rounded-radius-100 tablet:py-space-700 desktop:p-space-700',
  eyebrow:
    'flex items-center gap-space-200 tablet:h-9 text-eyebrow-small max-tablet:tracking-(--letter-spacing-base) text-text-body uppercase',
  title: 'text-h1 mt-space-500 tablet:mt-space-400',
  intro: 'text-body-xlarge mt-space-300 tablet:mt-space-400',
  sectionTitle: 'mt-space-500 tablet:mt-space-700 mb-space-300',
  sectionTitleHeading: 'block tablet:inline text-h4',
  sectionTitleNote: 'text-body',
  selectionError: 'text-body text-text-error mt-space-200',
  panel: 'min-h-150 has-[.gc-dg-layout]:min-h-0',
  mainColumn: 'desktop:max-w-(--checkout-main-column)',
  faqSection: 'mt-space-700 desktop:mt-10',
  faqTitle: 'text-h4 mb-space-500',
  faqList: 'flex flex-col gap-space-500 [&>div]:p-space-400 [&_button+div>div]:mt-space-300',
  faqBody: 'flex flex-col gap-space-400',
  recaptchaNotice: 'tablet:hidden text-body-small mt-space-500',
  summaryScrim: 'checkout-v2__summary-scrim fixed inset-0 z-10 bg-neutral-700 opacity-50',
};

/**
 * @description Checkout page layout for the experiment test variant: secure checkout card around
 * the checkout panel, with a back link and FAQs below it.
 */
export const CheckoutVariantLayout = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmation = useConfirmation();
  const summarySheet = useMobileSummarySheet(panelRef);
  const billingPaymentMethod = useBillingAddressPlacement(panelRef);
  useSdkCopy(panelRef, getAchPromotionText(confirmation?.applianceDetails?.arrivalDate));
  useSelectedMethodScroll(panelRef);
  useSummaryTotalCopy(panelRef);
  const tracking = useCheckoutTracking(panelRef);
  useFieldValidationState(panelRef, tracking.trackFormError);
  const itemBenefitsSlots = useSdkSlots(panelRef, ITEM_BENEFITS_SLOT);
  const billingHintSlots = useSdkSlots(panelRef, BILLING_HINT_SLOT);
  const consent = useConsentBoxes(panelRef);
  const paymentMethodsRef = useRef<HTMLDivElement>(null);
  const paymentSelection = usePaymentMethodSelection(panelRef, () =>
    smoothScrollIntoView(paymentMethodsRef.current),
  );

  const handleFaqLinkClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    smoothScrollIntoView(document.getElementById(FAQ_SECTION_ID));
  };

  return (
    <div
      className={`${styles.page} ${paymentSelection.hasSelectedMethod ? '' : 'checkout-v2--no-method'}`}
    >
      <div className={styles.card}>
        <p className={styles.eyebrow}>
          <Lock />
          Secure checkout
        </p>
        <h1 className={styles.title}>Almost done</h1>
        <p className={styles.intro}>
          Let's set up your {VITE_CLIENT_FRIENDLY_NAME} Appliance Care protection below.{' '}
          <TextLink href={`#${FAQ_SECTION_ID}`} onClick={handleFaqLinkClick}>
            Got questions? View FAQs
          </TextLink>
        </p>
        <div className={styles.sectionTitle} ref={paymentMethodsRef}>
          <h2 className={styles.sectionTitleHeading}>Select a payment method</h2>{' '}
          <span className={styles.sectionTitleNote}>Monthly plan, cancel anytime</span>
          {paymentSelection.isSelectionRequired && (
            <p role="alert" className={styles.selectionError}>
              Please select a payment method
            </p>
          )}
        </div>
        <div className={styles.panel} ref={panelRef}>
          {children}
        </div>
        {itemBenefitsSlots.map((slot, index) =>
          createPortal(
            <ul>
              {ITEM_BENEFITS.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>,
            slot,
            `benefits-${index}`,
          ),
        )}
        {billingPaymentMethod &&
          billingHintSlots.map((slot, index) =>
            createPortal(
              <CheckoutBillingHint paymentMethod={billingPaymentMethod} />,
              slot,
              `billing-hint-${index}`,
            ),
          )}
        {consent.consentBoxes.map((consentBox) => (
          <CheckoutConsent
            key={consentBox.checkboxId}
            consentBox={consentBox}
            paymentStartDate={consent.paymentStartDate}
          />
        ))}
        <div className={styles.mainColumn}>
          <div className="checkout-v2__back">
            <TextLink
              as="button"
              type="button"
              onClick={() => navigate(AppRoute.QUOTE)}
              leftIcon={<ArrowLeft />}
            >
              Go back
            </TextLink>
          </div>
          <p className={styles.recaptchaNotice}>
            This site is protected by reCAPTCHA and the Google{' '}
            <TextLink href="https://policies.google.com/privacy">Privacy Policy</TextLink> and{' '}
            <TextLink href="https://policies.google.com/terms">Terms of Service</TextLink> apply.
          </p>
          <section
            id={FAQ_SECTION_ID}
            aria-labelledby={FAQ_HEADING_ID}
            className={styles.faqSection}
          >
            <h2 id={FAQ_HEADING_ID} className={styles.faqTitle}>
              Frequently Asked Questions
            </h2>
            <div className={styles.faqList}>
              {CHECKOUT_FAQ_ITEMS.map((item) => (
                <Accordion
                  key={item.id}
                  title={item.title}
                  icon="chevron"
                  onToggle={(expanded) => tracking.trackFaqToggle(item.title, expanded)}
                >
                  <div className={styles.faqBody}>
                    {item.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </Accordion>
              ))}
            </div>
          </section>
        </div>
      </div>
      {summarySheet.isExpanded && (
        <div className={styles.summaryScrim} aria-hidden="true" onClick={summarySheet.collapse} />
      )}
    </div>
  );
};
